import React, { useState, useEffect } from "react";
import {
  Box, Grid, Card, CardContent, Typography, IconButton, Button, Divider
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

function CalendarWidget() {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        <CalendarMonthIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Prochains RDV
      </Typography>
      <Typography variant="body2">29 sept. 10:00 : RDV client Dupont</Typography>
      <Typography variant="body2">30 sept. 14:00 : Chantier toiture</Typography>
    </>
  );
}

function StatsWidget() {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        <TrendingUpIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Objectifs du mois
      </Typography>
      <Typography variant="body2">Objectif : 10 devis acceptés</Typography>
      <Typography variant="body2">Réalisé : 7</Typography>
      <Typography variant="body2">Reste : 3</Typography>
    </>
  );
}

function WeatherWidget() {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        <WbSunnyIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Météo locale
      </Typography>
      <Typography variant="body2">Lille : 23°C, Ensoleillé ☀️</Typography>
    </>
  );
}

function AdviceWidget() {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        <LightbulbIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Assistant IA
      </Typography>
      <Typography variant="body2">💡 Relancez vos clients 2 jours après envoi d un devis pour doubler vos chances de signature.</Typography>
      <Button 
        size="small" 
        variant="outlined" 
        sx={{ mt: 1 }}
        onClick={() => window.dispatchEvent(new CustomEvent('openChatBot'))}
      >
        Ouvrir Assistant
      </Button>
    </>
  );
}

function InvoicesWidget() {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        <ReceiptLongIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Factures à relancer
      </Typography>
      <Typography variant="body2">Facture #2024-012 (Mme Lefevre) : 900€ en retard</Typography>
    </>
  );
}

const WIDGETS_CATALOG = [
  { id: "calendar", name: "Calendrier", comp: <CalendarWidget /> },
  { id: "stats", name: "Objectifs", comp: <StatsWidget /> },
  { id: "weather", name: "Météo", comp: <WeatherWidget /> },
  { id: "advice", name: "Assistant IA", comp: <AdviceWidget /> },
  { id: "invoices", name: "Factures à relancer", comp: <InvoicesWidget /> },
];

export default function Dashboard() {
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem("widgets_order_v2");
    if (saved) return JSON.parse(saved);
    return [ "calendar", "stats", "weather" ];
  });

  useEffect(() => {
    localStorage.setItem("widgets_order_v2", JSON.stringify(widgets));
  }, [widgets]);

  function handleAddWidget(id) {
    if (!widgets.includes(id)) setWidgets([...widgets, id]);
  }

  function handleRemoveWidget(id) {
    setWidgets(widgets.filter((w) => w !== id));
  }

  function handleDragEnd(result) {
    if (!result.destination) return;
    const items = Array.from(widgets);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setWidgets(items);
  }

  const widgetComps = widgets
    .map((id) => {
      const found = WIDGETS_CATALOG.find((w) => w.id === id);
      if (!found) return null;
      return { ...found };
    })
    .filter(Boolean);

  const availableWidgets = WIDGETS_CATALOG.filter((w) => !widgets.includes(w.id));

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 4 }}>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Tableau de bord personnalisé
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          variant="outlined"
          disabled={availableWidgets.length === 0}
          sx={{ mr: 2 }}
          onClick={() => {
            if (availableWidgets.length > 0) handleAddWidget(availableWidgets[0].id);
          }}
        >
          Ajouter un widget
        </Button>
        {availableWidgets.map((w) => (
          <Button
            key={w.id}
            variant="text"
            size="small"
            onClick={() => handleAddWidget(w.id)}
          >
            {w.name}
          </Button>
        ))}
      </Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="dashboard-widgets" direction="horizontal">
          {(provided) => (
            <Grid
              container
              spacing={3}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {widgetComps.map((w, idx) => (
                <Draggable draggableId={w.id} index={idx} key={w.id}>
                  {(prov, snapshot) => (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                      sx={{
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        cursor: "grab",
                        transition: "box-shadow 0.2s",
                        boxShadow: snapshot.isDragging
                          ? "0 4px 20px rgba(0,0,0,0.18)"
                          : "none",
                        position: "relative",
                      }}
                    >
                      <Card>
                        <CardContent>
                          <IconButton
                            size="small"
                            sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
                            onClick={() => handleRemoveWidget(w.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          {w.comp}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
}
